import { useContext, useRef, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import Utils from '@/utils/util';
import { Event } from '@/features/events';
import { UserContext } from '@/routes';
import { ServiceTip } from '../types';
import { Heading } from '@/components/Elements/Heading';
import { TableData } from '@/components/Elements/Table/TableData';
import { ServiceTypes } from '@/components/Elements/ServiceTypes';
import { ServiceTipCard } from './ServiceTipCard';
import UserIcon from '@/assets/img/user-default-icon.png';
import { ReactComponent as SearchIcon } from '@/assets/img/search-iocn-v2.svg';
import { ReactComponent as MenuBackIcon } from '@/assets/img/menu-back-icon.svg';

import { useGetServiceTipDetails, UseServiceTipOptions } from '../api/getServiceTips';
import PaymentRequest from '@/features/events/components/PaymentRequest';
import './TipsDetails.scss';
import { useAuth } from '@/lib/auth';
import { businessCurrency } from '@/features/Language/api/getCountryCurrency';

export const ServiceTips = () => {
  const userContext = useContext(UserContext);
  const { user } = useAuth();
  const navigate = useNavigate();
  const currency = businessCurrency(user, 'USD');
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [tableCurrentIndex, setTableCurrentIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const perPage = 30;
  const [serviceTipList, setServiceTipList] = useState<ServiceTip[]>([]);
  const [searchString, setSearchString] = useState<string>('');
  const [debounceSearchString, setDebouneSearchString] = useState<string>(searchString);
  const [selectedAppointment, setSelectedAppointment] = useState<Event>();
  const onTableStateChange = (pageIndex: number) => {
    fetchServiceTips(pageIndex);
  };
  const getServiceTipsMutation = useGetServiceTipDetails();
  // useEffect(() => {
  //   fetchServiceTips(0, true);
  // }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouneSearchString(searchString);
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchString]);

  useEffect(() => {
    fetchServiceTips(0, true);
  }, [debounceSearchString]);

  const fetchServiceTips = async (pageIndex: number, reset = false) => {
    if (id !== 'null') {
      const req: UseServiceTipOptions = {
        id: id,
        perPage,
        page: pageIndex,
        order: 'desc',
        sort: 'paymentDate',
        search: debounceSearchString,
      };
      const res = await getServiceTipsMutation.mutateAsync(req);
      setTotalPages(res.totalPages);
      setTotalRecords(res.total);
      if (reset) {
        setServiceTipList(res.list);
        setTableCurrentIndex(1);
        userContext && !userContext?.isLandscape && window.scrollTo(0, 0);
      } else {
        setServiceTipList(serviceTipList.concat(res.list));
        setTableCurrentIndex(pageIndex);
      }
    }
  };

  const handlePageClick = async (event: any) => {
    setServiceTipList([]);
    setSelectedPage(event.selected + 1);
    fetchServiceTips(event.selected + 1, true);
  };
  const searchTips = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchString = e.target.value;
    setSearchString(searchString);
  };
  const startIndex = selectedPage == 1 ? 1 : (selectedPage - 1) * perPage + 1;
  const endIndex =
    selectedPage == 1
      ? 1 * perPage
      : selectedPage == totalPages
      ? totalRecords
      : selectedPage * perPage;

  const paginationText = `Showing ${startIndex} to ${endIndex} of ${totalRecords} entries`;
  const actualServiceList = serviceTipList;
  const showPaymentDetails = (data: ServiceTip) => {
    setSelectedAppointment(data.appointment);
  };
  return (
    <section className="service-tips container-bg">
      {(!userContext?.isLandscape || userContext?.isLandscape) && (
        <div className="content">
          <div
            className="menu-back-nav"
            onClick={() => {
              navigate('/calender');
            }}
          >
            <MenuBackIcon /> <span>Back</span>
          </div>
          <div className="card-top-header">
            <Heading
              title="Service Tip Details"
              description="Manage your team members tips and their details here"
            />
            <span className="search-content">
              <SearchIcon />
              <input type="text" id="simple-search" placeholder="Search" onChange={searchTips} />
            </span>
          </div>

          <div className="service-tip-list petsked-table">
            {userContext?.isLandscape ? (
              <TableData<ServiceTip>
                data={actualServiceList}
                columns={[
                  {
                    title: 'Customer',
                    field: 'appointmentId',
                    Cell({ entry: data }) {
                      return (
                        <div className="customer-details">
                          <img
                            src={
                              data?.appointment?.petParent?.avatarUrl
                                ? data?.appointment?.petParent?.avatarUrl
                                : UserIcon
                            }
                          />
                          <div className="details">
                            <div className="name">{data?.appointment?.petParent?.name}</div>
                            <a onClick={() => showPaymentDetails(data)}>
                              Appt ID #{data?.appointmentId}
                            </a>
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    title: 'Services',
                    field: 'paymentDate',
                    Cell({ entry: { appointment } }) {
                      return (
                        <ServiceTypes
                          services={appointment.service ? [appointment.service] : []}
                          restrictService={true}
                        />
                      );
                    },
                  },
                  {
                    title: 'Payment Date',
                    field: 'paymentDate',
                    Cell({ entry: { paymentDate } }) {
                      return (
                        <span className="default">
                          {Utils.getOnlyDate(paymentDate, 'MMM DD, yyyy')}
                          <span className="date-seperator"></span>

                          {Utils.getAMPMtimeWithoutFormat(paymentDate)}
                        </span>
                      );
                    },
                  },

                  {
                    title: 'Tip Amount',
                    field: 'serviceTip',
                    Cell({ entry: { serviceTip } }) {
                      return (
                        <span className="default dark">
                          {currency}
                          {Utils.formatAmount(serviceTip)}{' '}
                        </span>
                      );
                    },
                  },
                ]}
                tableInitialState={{
                  tableTotalRecords: totalPages,
                  tablePageIndex: tableCurrentIndex,
                }}
                onTableStateChange={onTableStateChange}
                isDataLoading={getServiceTipsMutation.isLoading}
              />
            ) : (
              <>
                {actualServiceList.map((asl: any, index) => (
                  <>
                    <ServiceTipCard key={`service-card-${index}`} data={asl} />
                    {totalPages > 1 && (
                      <div className="paginate">
                        <p className="description">{paginationText}</p>
                        <div className="pagination-stores">
                          <ReactPaginate
                            nextLabel=""
                            onPageChange={handlePageClick}
                            pageRangeDisplayed={3}
                            marginPagesDisplayed={2}
                            pageCount={totalPages}
                            previousLabel=""
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item previous-button"
                            previousLinkClassName="page-link"
                            nextClassName="page-item next-button"
                            nextLinkClassName="page-link"
                            breakLabel="..."
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            containerClassName="pagination"
                            activeClassName="active"
                            forcePage={selectedPage - 1}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ))}
              </>
            )}
          </div>
        </div>
      )}
      {selectedAppointment && (
        <PaymentRequest
          open={selectedAppointment ? true : false}
          onClose={(refresh: boolean) => {
            setSelectedAppointment(undefined);
          }}
          selectedAppointment={selectedAppointment}
        />
      )}
    </section>
  );
};
